/**
 * CircularBuffer.js
 * Buffer circular O(1) para operacoes push/shift
 * Compativel com Redux/Immer (estrutura serializavel)
 *
 * Estrutura: { buffer: Array, head: number, length: number, size: number }
 *
 * Complexidade:
 * - push: O(1)
 * - toArray: O(n)
 * - get(index): O(1)
 *
 * Uso:
 *   const cb = CircularBuffer.create(10);
 *   CircularBuffer.push(cb, [lng, lat]);
 *   const array = CircularBuffer.toArray(cb);
 */

const CircularBuffer = {
  /**
   * Cria um novo circular buffer
   * @param {number} size - Tamanho maximo do buffer
   * @returns {Object} Estrutura do buffer
   */
  create(size) {
    return {
      buffer: new Array(size).fill(null),
      head: 0,
      length: 0,
      size,
    };
  },

  /**
   * Adiciona item ao buffer (sobrescreve mais antigo se cheio)
   * @param {Object} cb - Circular buffer
   * @param {*} item - Item a adicionar
   */
  push(cb, item) {
    const index = (cb.head + cb.length) % cb.size;
    cb.buffer[index] = item;

    if (cb.length < cb.size) {
      cb.length++;
    } else {
      // Buffer cheio: move head para frente (descarta mais antigo)
      cb.head = (cb.head + 1) % cb.size;
    }
  },

  /**
   * Retorna ultimo item adicionado
   * @param {Object} cb - Circular buffer
   * @returns {*} Ultimo item ou null
   */
  last(cb) {
    if (cb.length === 0) return null;
    const index = (cb.head + cb.length - 1) % cb.size;
    return cb.buffer[index];
  },

  /**
   * Retorna item por indice (0 = mais antigo)
   * @param {Object} cb - Circular buffer
   * @param {number} index - Indice do item
   * @returns {*} Item ou undefined
   */
  get(cb, index) {
    if (index < 0 || index >= cb.length) return undefined;
    return cb.buffer[(cb.head + index) % cb.size];
  },

  /**
   * Converte buffer para array ordenado (mais antigo primeiro)
   * @param {Object} cb - Circular buffer
   * @returns {Array} Array com itens em ordem
   */
  toArray(cb) {
    if (cb.length === 0) return [];

    const result = new Array(cb.length);
    for (let i = 0; i < cb.length; i++) {
      result[i] = cb.buffer[(cb.head + i) % cb.size];
    }
    return result;
  },

  /**
   * Limpa o buffer
   * @param {Object} cb - Circular buffer
   */
  clear(cb) {
    cb.buffer.fill(null);
    cb.head = 0;
    cb.length = 0;
  },

  /**
   * Verifica se buffer esta vazio
   * @param {Object} cb - Circular buffer
   * @returns {boolean}
   */
  isEmpty(cb) {
    return cb.length === 0;
  },

  /**
   * Verifica se buffer esta cheio
   * @param {Object} cb - Circular buffer
   * @returns {boolean}
   */
  isFull(cb) {
    return cb.length === cb.size;
  },

  /**
   * Migra array existente para circular buffer
   * @param {Array} array - Array existente
   * @param {number} size - Tamanho do buffer
   * @returns {Object} Circular buffer com dados migrados
   */
  fromArray(array, size) {
    const cb = CircularBuffer.create(size);
    // Pega apenas os ultimos 'size' elementos se array for maior
    const start = Math.max(0, array.length - size);
    for (let i = start; i < array.length; i++) {
      CircularBuffer.push(cb, array[i]);
    }
    return cb;
  },
};

export default CircularBuffer;
